// pages/CriticalAlertsAndInsights.jsx
import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Alert,
  Spinner,
} from 'reactstrap';
import {
  FaSync,
} from 'react-icons/fa';
import { useAlerts } from '../../Hooks/useAlerts';
import AlertTable from '../../Components/criticalAlertsAndInsights/AlertTable';
import AlertSummaryCards from '../../Components/criticalAlertsAndInsights/AlertSummaryCards';
import InsightsCard from '../../Components/criticalAlertsAndInsights/InsightsCard';
import AlertConfigCard from '../../Components/criticalAlertsAndInsights/AlertConfigCard';
import AlertDetailModal from '../../Components/criticalAlertsAndInsights/AlertDetailModal';
import AlertRulesModal from '../../Components/criticalAlertsAndInsights/AlertRulesModal';

const ITEMS_PER_PAGE = 5;

const CriticalAlertsAndInsights = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState('Todos los Estados');
  const [filterLevel, setFilterLevel] = useState('Todos los Niveles');
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isConfigOpen, setIsConfigOpen] = useState(false);

  const {
    alerts,
    pagination,
    summary,
    insights,
    insightsLoading,
    insightsError,
    isLoading,
    error,
    updateStatus,
    isUpdatingStatus,
    runEngine,
    isRunning,
    runError,
  } = useAlerts({
    page: currentPage,
    limit: ITEMS_PER_PAGE,
    level: filterLevel,
    status: filterStatus,
    search: debouncedSearch,
  });

  const handleSearch = (event) => {
    if (event.key === 'Enter') {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }
  };

  const changeFilter = (setter) => (event) => {
    setter(event.target.value);
    setCurrentPage(1);
  };

  const handleViewAlert = (alert) => {
    setSelectedAlert(alert);
  };

  const handleMarkAsResolved = (alertId) => {
    updateStatus(alertId, 'Resuelta');
  };

  return (
    <Container fluid className="mt-4">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h4 className="fw-bold mb-1 mt-3">Alertas e Inteligencia Operativa</h4>
          <small className="text-muted">
            Monitoreo inteligente y alertas configurables
            {summary?.last_run_at && (
              <>
                {' · '}Última actualización:{' '}
                {new Date(summary.last_run_at).toLocaleString('es-CO', {
                  timeZone: 'America/Bogota',
                })}
              </>
            )}
          </small>
        </div>
        <Button
          color="light"
          size="sm"
          onClick={runEngine}
          disabled={isRunning}
          title="Vuelve a evaluar todas las reglas activas"
          className="border mt-3"
        >
          {isRunning ? (
            <>
              <Spinner size="sm" className="me-1" /> Evaluando...
            </>
          ) : (
            <>
              <FaSync className="me-1" size={12} /> Actualizar alertas
            </>
          )}
        </Button>
      </div>

      {runError && (
        <Alert color="danger" className="py-2">
          <small>{runError.message}</small>
        </Alert>
      )}
      {error && (
        <Alert color="danger" className="py-2">
          <small>{error.message}</small>
        </Alert>
      )}

      <AlertSummaryCards summary={summary} />

      <Row>
        <Col lg="8">
          <AlertTable
            alerts={alerts}
            pagination={pagination}
            isLoading={isLoading}
            filterLevel={filterLevel}
            filterStatus={filterStatus}
            searchTerm={searchTerm}
            onFilterLevelChange={changeFilter(setFilterLevel)}
            onFilterStatusChange={changeFilter(setFilterStatus)}
            onSearchChange={(e) => setSearchTerm(e.target.value)}
            onSearchEnter={handleSearch}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            updateStatus={updateStatus}
            isUpdatingStatus={isUpdatingStatus}
            onViewAlert={handleViewAlert}
          />
        </Col>

        <Col lg="4">
          <InsightsCard
            insights={insights}
            isLoading={insightsLoading}
            error={insightsError}
          />
          <AlertConfigCard onOpenConfig={() => setIsConfigOpen(true)} />
        </Col>
      </Row>
      <AlertDetailModal
        isOpen={Boolean(selectedAlert)}
        toggle={() => setSelectedAlert(null)}
        alert={selectedAlert}
        onMarkAsResolved={handleMarkAsResolved}
      />
      <AlertRulesModal
        isOpen={isConfigOpen}
        toggle={() => setIsConfigOpen(false)}
      />
    </Container>
  );
};

export default CriticalAlertsAndInsights;